import {
  mqttConnectionDetailsAtom,
  mqttInitializationStatusAtom,
} from '@store/atoms/mqtt/connection/mqttConnectionAtom';
import { multiTopicSubscriptionAtomFamily } from '@store/atoms/mqtt/subscription/multiTopicSubscriptionAtom';
import { subscribeWithLifecycleAtom } from '@store/atoms/mqtt/subscription/subscribeWithLifecycleAtom';
import { topicDataAtomFamily } from '@store/atoms/mqtt/subscription/subscriptionAtoms';
import { unsubscribeWithLifecycleAtom } from '@store/atoms/mqtt/subscription/unsubscribeWithLifecycleAtom';
import type {
  MqttMessage,
  SubscriptionConfig,
  UseMqttSubscriptionMultipleResult,
  UseMqttSubscriptionSingleResult,
} from '@store/atoms/mqtt/types';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  generateMultiSubscriberId,
  generateSubscriberId,
  mqttLogger,
} from './utils';

export function useMqttSubscription<T = unknown>(
  topic: string,
  options?: SubscriptionConfig,
): UseMqttSubscriptionSingleResult<T>;

export function useMqttSubscription<T = unknown>(
  topics: string[],
  options?: SubscriptionConfig,
): UseMqttSubscriptionMultipleResult<T>;

export function useMqttSubscription<T = unknown>(
  topics: string | string[],
  options?: SubscriptionConfig,
): UseMqttSubscriptionSingleResult<T> | UseMqttSubscriptionMultipleResult<T> {
  const isArray = Array.isArray(topics);
  const store = useStore();

  const validTopics = useMemo(() => {
    if (isArray) {
      return topics.filter((topic) => topic?.trim());
    }
    return topics?.trim() || '';
  }, [topics, isArray]);

  const subscriptionData = useAtomValue(
    isArray
      ? multiTopicSubscriptionAtomFamily(validTopics as string[])
      : topicDataAtomFamily(validTopics as string),
  );

  const subscribeToTopic = useSetAtom(subscribeWithLifecycleAtom);
  const unsubscribeFromTopic = useSetAtom(unsubscribeWithLifecycleAtom);
  const initStatus = useAtomValue(mqttInitializationStatusAtom);
  const connectionDetails = useAtomValue(mqttConnectionDetailsAtom);

  const isLoading =
    initStatus === 'initializing' ||
    initStatus === 'idle' ||
    connectionDetails.status === 'connecting' ||
    connectionDetails.status === 'reconnecting';

  const multiSubscriberIdRef = useRef<string>(generateMultiSubscriberId());
  const onMessageRef = useRef(options?.onMessage);
  onMessageRef.current = options?.onMessage;

  const subscriptionConfig = useMemo(
    () => ({
      parseJson: options?.parseJson,
      qos: options?.qos,
      validator: options?.validator,
      transform: options?.transform,
    }),
    [options?.parseJson, options?.qos, options?.validator, options?.transform],
  );

  const createAtomSubscription = useCallback(
    (topic: string): (() => void) => {
      const atom = topicDataAtomFamily(topic);
      return store.sub(atom, () => {
        const message = store.get(atom);
        if (message && onMessageRef.current) {
          try {
            onMessageRef.current(topic, message);
          } catch (error) {
            mqttLogger.error(`onMessage callback error for ${topic}:`, error);
          }
        }
      });
    },
    [store],
  );

  useEffect(() => {
    if (options?.enabled === false) {
      return;
    }

    const topicsToSubscribe = isArray
      ? (validTopics as string[])
      : [validTopics as string];
    const filteredTopics = topicsToSubscribe.filter(Boolean);

    if (filteredTopics.length === 0) {
      return;
    }

    const subscriberId = isArray
      ? multiSubscriberIdRef.current
      : generateSubscriberId();

    // Defer subscriptions to not block initial render
    queueMicrotask(() => {
      filteredTopics.forEach((topic) => {
        subscribeToTopic({
          topic,
          subscriberId,
          config: subscriptionConfig,
        }).catch((error) => {
          mqttLogger.error(`Failed to subscribe to topic ${topic}:`, error);
        });
      });
    });

    const jotaiUnsubscribes = onMessageRef.current
      ? filteredTopics.map(createAtomSubscription)
      : [];

    return () => {
      // Defer all cleanup to not block route transitions - run in background
      queueMicrotask(() => {
        jotaiUnsubscribes.forEach((unsub) => unsub());
        filteredTopics.forEach((topic) => {
          unsubscribeFromTopic({
            topic,
            subscriberId,
          });
        });
      });
    };
  }, [
    validTopics,
    isArray,
    options?.enabled,
    subscriptionConfig,
    subscribeToTopic,
    unsubscribeFromTopic,
    createAtomSubscription,
  ]);

  if (isArray) {
    const multiData = subscriptionData as Record<string, MqttMessage<T> | null>;
    return {
      data: multiData,
      isLoading,
    };
  }

  const singleMessage = subscriptionData as MqttMessage<T> | null;
  return {
    value: singleMessage?.value ?? null,
    timestamp: singleMessage?.timestamp,
    topic: topics as string,
    error: singleMessage?.error,
    isLoading,
  };
}
